<template>
  <div class="column-content-container">
    <div class="options-row" id="options-row">
      <button
        v-for="(option, index) in options"
        :key="index"
        class="h3-defaultMuni"
        :class="{ activo: selected === option }"
        @click="selected = option"
      >
        {{ option.title }}
      </button>
    </div>

    <div class="statistics-card" id="statistics-card" v-if="!loading">
      <div class="statistics-btns-row h2-heroMuni" id="statistics-controls">
        <div class="options">
          <button
            class="ia-btn"
            @click="toggleAnalysis"
            :class="showAnalysis ? 'green' : 'rose'"
          >
            {{ showAnalysis ? "Ver Tabla" : "Analizar IA" }}
          </button>
          <button class="muni-section-btn">Todo el Municipio</button>
          <img
            :src="logoDownload"
            @click="downloadReport"
            style="cursor: pointer"
            v-if="haveData"
            title="Descargar Reporte"
          />
        </div>
        <div class="period-btn-container">
          <button class="period-btn">
            <i class="bi bi-caret-left-fill" @click="changeMonth(-1)"></i>
            {{ formattedDate }}
            <i
              class="bi bi-caret-right-fill"
              @click="isNextMonthDisabled ? null : changeMonth(1)"
            ></i>
          </button>
        </div>
      </div>

      <div class="statistics-info" v-if="haveData">
        <div v-if="showAnalysis" class="statistics-table">
          <div class="content-wrapper" v-if="analysis">
            <p class="h2-defaultMuni">📊<b> Análisis</b></p>
            <p class="h2-defaultMuni">{{ analysis }}</p>
          </div>
          <div class="stacked-bars" v-else>
            <div class="content-wrapper stacked-bars">
              <LoadingComponent />
            </div>
          </div>
        </div>
        <div class="statistics-table" v-if="tableData && !showAnalysis">
          <div class="content-wrapper">
            <table class="simplified-muni-table">
              <thead class="h2-heroMuni">
                <tr>
                  <th
                    v-for="header in tableData.headers"
                    :key="header.title"
                    :style="header.style"
                  >
                    {{ header.title }}
                  </th>
                </tr>
              </thead>
            </table>
            <div
              class="table-body-scroll"
              :style="{
                marginBottom: tableData.data.length < 4 ? '-0.62rem' : 'unset',
              }"
            >
              <table class="simplified-muni-table">
                <tbody class="h3-heroMuni">
                  <tr v-for="(row, rowIndex) in tableData.data" :key="rowIndex">
                    <td
                      v-for="(cell, cellIndex) in row"
                      :key="cellIndex"
                      :style="cell.style"
                    >
                      <span class="truncated-text">{{ cell.value }}</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <table class="simplified-muni-table" id="table-footer">
              <thead class="h2-heroMuni">
                <tr>
                  <th
                    v-for="(total, key) in tableData.footer"
                    :key="key"
                    :style="total.style"
                  >
                    {{ total.value }}
                  </th>
                </tr>
              </thead>
            </table>
          </div>
        </div>
        <div class="statistics-charts">
          <div v-if="pieChartData && selected.config.set" class="pie">
            <PieChart
              :data="pieChartData"
              :labels="selected.config.set.labels"
              :colors="selected.config.set.colors"
            />
          </div>

          <div
            :class="[
              'stacked-bars-container',
              {
                'full-height-chart': !pieChartData,
              },
            ]"
            style="width: 100%; overflow-x: auto; overflow-y: hidden"
          >
            <div
              v-if="stackedBarChartData && selected.config?.set"
              class="stacked-bars"
              :style="{ width: calculateStackedBarWidth }"
            >
              <StackedBarChart
                :key="stackedBarKey"
                :data="stackedBarChartData"
                :stackLabels="selected.config.set.labels.slice().reverse()"
                :colors="selected.config.set.colors.slice().reverse()"
              />
            </div>
          </div>
        </div>
      </div>
      <div class="statistics-info" v-else>
        <div class="no-data">
          <h1 class="h1-heroMuni">¡Todavía no hay información disponible</h1>
          <h1 class="h1-heroMuni">para generar una estadística!</h1>
        </div>
      </div>
    </div>
    <div class="statistics-card" v-else>
      <LoadingComponent />
    </div>
  </div>

  <ModalManager ref="modalManagerRef" />
</template>

<script setup>
import StatisticsService from "@/services/office/statisticsService";
import { ref, onMounted, watch, computed, nextTick } from "vue";
import StackedBarChart from "./Graficos/StackedBarChart.vue";
import PieChart from "./Graficos/PieChart.vue";
import LoadingComponent from "../Modals/Carga/LoadingComponent.vue";
import { SocketOffice } from "@/services/sockets/officeSocket";
import N8nService from "@/services/n8n/n8nService";
import logoDownload from "@/assets/images/LuqueLogoDownload.svg";
import html2pdf from "html2pdf.js";
import { useToast } from "vue-toastification";
import ModalManager from "@/components/Utils/Modales/ModalManager.vue";
// 1. CONFIGURACIÓN CENTRALIZADA
const options = ref([
  {
    title: "Trámites por agente y plazo",
    reportType: "byAgentAndPeriod",
    config: {
      // Datos
      dataKey: "byMunicipal", // Dónde leer el array en info.value
      labelKey: "municipalName", // Propiedad para el nombre
      valueKeys: ["late", "onTime"], // Propiedades para las barras
      // Visual
      set: {
        labels: ["En Plazo", "Fuera de Plazo"],
        colors: ["rgba(26, 188, 156, 1)", "rgba(245, 59, 87, 1)"],
      },
      // Lógica de espacio
      spacing: (count) => (count <= 4 ? 70 : 90),
    },
  },
  {
    title: "Trámites por categoría y canal",
    reportType: "byCategoryAndChannel",
    config: {
      dataKey: "byCategory",
      labelKey: "categoryName",
      valueKeys: ["presencial", "web"],
      set: {
        labels: ["Web", "Presencial"],
        colors: ["rgba(255, 185, 0, 1)", "rgba(155, 154, 154, 1)"],
      },
      spacing: (count) => (count <= 4 ? 70 : 90),
      limit: 10,
    },
  },
  {
    title: "Ranking de trámites",
    reportType: "proceduresRanking",
    config: {
      dataKey: "byProcedure",
      labelKey: "procedureName",
      valueKeys: ["totalProcedures"],
      set: {
        labels: ["Trámites"],
        colors: ["rgba(0, 195, 168, 1)"],
      },
      spacing: (count) => (count <= 4 ? 70 : 100),
      limit: 10,
    },
  },
]);

const selected = ref(options.value[0]);
const info = ref(null);
const today = ref(new Date());
const stackedBarKey = ref(0);
const loading = ref(false);
const haveData = ref(false);
const analysis = ref(null);
const showAnalysis = ref(false);
const modalManagerRef = ref(null);

const toast = useToast();

const formattedDate = computed(() => {
  const options = { year: "numeric", month: "long" };
  return new Intl.DateTimeFormat("es-ES", options)
    .format(today.value)
    ?.replace(" de", "");
});

// 2. DATOS PROCESADOS PARA EL GRÁFICO (Ordenados y Limitados)
const chartSourceData = computed(() => {
  if (!info.value) return [];

  const config = selected.value.config;
  const rawList = info.value[config.dataKey]; // Leemos dinámicamente

  if (!Array.isArray(rawList)) return [];

  // Creamos copia para no mutar original (importante para la tabla)
  let list = [...rawList];

  // Ordenamos descendente por suma de valores
  list.sort((a, b) => {
    const sumA = config.valueKeys.reduce((acc, key) => acc + (a[key] || 0), 0);
    const sumB = config.valueKeys.reduce((acc, key) => acc + (b[key] || 0), 0);
    return sumB - sumA;
  });

  // Aplicamos límite si existe en la config
  if (config.limit && list.length > config.limit) {
    list = list.slice(0, config.limit);
  }

  return list;
});

// 3. CÁLCULO DE ANCHO (Basado en datos recortados)
const calculateStackedBarWidth = computed(() => {
  const config = selected.value.config;
  const barCount = chartSourceData.value.length;

  if (barCount === 0) return "100%";

  const separationValue =
    typeof config.spacing === "function"
      ? config.spacing(barCount)
      : config.spacing || 90;

  return `${barCount * separationValue}px`;
});

// 4. DATA PARA EL GRÁFICO DE BARRAS (Mapeo genérico)
const stackedBarChartData = computed(() => {
  const dataList = chartSourceData.value;
  if (!dataList.length) return null;

  const config = selected.value.config;

  return dataList.map((item) => ({
    label: item[config.labelKey], // ej: item.municipalName
    values: config.valueKeys.map((key) => item[key] || 0), // ej: [item.late, item.onTime]
  }));
});

const isNextMonthDisabled = computed(() => {
  const currentMonth = new Date().getMonth();
  const currentYear = new Date().getFullYear();
  return (
    today.value.getMonth() >= currentMonth &&
    today.value.getFullYear() >= currentYear
  );
});

const fetchStatistics = async () => {
  analysis.value = null;
  const startDate = new Date(
    today.value.getFullYear(),
    today.value.getMonth(),
    1,
  );
  const endDate = new Date(
    today.value.getFullYear(),
    today.value.getMonth() + 1,
    0,
    23,
    59,
    59,
    999,
  );

  const startISO = startDate.toISOString();
  const endISO = endDate.toISOString();

  const body = {
    reportType: selected.value.reportType,
    startDate: startISO,
    endDate: endISO,
  };

  const response = await StatisticsService.byType(body);
  if (!response?.success) {
    info.value = null;
    return;
  }
  info.value = response?.data ?? null;
  haveData.value = response?.data?.totalProcedures ?? false;
  getAnalysis();
};

const getAnalysis = async () => {
  if (haveData.value) {
    const res = await N8nService.getStatsAnalysis(info.value);
    if (!res?.success) return;
    analysis.value = res.data ?? null;
    stackedBarKey.value++;
  }
};

const toggleAnalysis = () => {
  showAnalysis.value = !showAnalysis.value;
  stackedBarKey.value++;
};

const changeMonth = async (direction) => {
  today.value = new Date(
    today.value.getFullYear(),
    today.value.getMonth() + direction,
  );
  loadStatistics();
};

watch(selected, async (newSelected, oldSelected) => {
  if (newSelected === oldSelected) return;
  loadStatistics();
});

onMounted(async () => {
  loadStatistics(false);
  SocketOffice?.on("procedures:update", () => {
    loadStatistics();
  });
});

const loadStatistics = async (reBuild = true) => {
  loading.value = true;
  await fetchStatistics();
  if (reBuild) stackedBarKey.value++;
  loading.value = false;
};

const pieChartData = computed(() => {
  if (!info.value) return null;

  if (selected.value.reportType === "byAgentAndPeriod") {
    return {
      labels: ["En Plazo", "Fuera de Plazo"],
      datasets: [
        {
          label: "Cant.",
          data: [info.value.totalOnTime | 0, info.value.totalLate | 0],
          backgroundColor: selected.value.config.set.colors,
          borderWidth: 2,
          borderColor: "var(--bg-popup)",
        },
      ],
    };
  }

  if (selected.value.reportType === "byCategoryAndChannel") {
    if (!info.value?.totalWeb && !info.value?.totalPresencial) return null;
    return {
      labels: ["Web", "Presencial"],
      datasets: [
        {
          label: "Cant.",
          data: [info.value.totalWeb | 0, info.value.totalPresencial | 0],
          backgroundColor: selected.value.config.set.colors,
          borderWidth: 2,
          borderColor: "var(--bg-popup)",
        },
      ],
    };
  }

  return null;
});

// --- LÓGICA DE TABLA (Se mantiene igual para mostrar TODOS los datos) ---
const tableData = computed(() => {
  if (!info.value) return null;
  let headers = [];
  let data = [];
  let footer = [];
  const datos = info.value;
  const totalProcedures = info.value.totalProcedures;
  const limit = selected.value.config.limit; // Obtenemos el límite

  if (
    selected.value.reportType === "byAgentAndPeriod" &&
    info.value?.byMunicipal
  ) {
    headers = [
      { title: "Agente", style: { width: "30%" } },
      { title: "Trámites", style: {} },
      { title: "En Plazo", style: {} },
      { title: "Aporte", style: {} },
    ];

    data = info.value.byMunicipal
      .map((agent) => {
        const participation =
          totalProcedures > 0
            ? (agent.totalProcedures / totalProcedures) * 100
            : 0;

        return {
          agentName: {
            value: agent.municipalName,
            style: { width: "30%", textAlign: "left" },
          },
          totalProcedures: {
            value: agent.totalProcedures,
            style: { textAlign: "center" },
          },
          onTime: {
            value: agent.onTime,
            style: { textAlign: "center" },
          },
          participation: {
            value: Math.round(participation) + "%",
            style: { textAlign: "center" },
          },
        };
      })
      .sort((a, b) => b.totalProcedures.value - a.totalProcedures.value);

    footer = [
      { value: "TOTAL", style: { width: "30%", textAlign: "center" } },
      { value: `${totalProcedures}`, style: { textAlign: "center" } },
      { value: `${datos.totalOnTime}`, style: { textAlign: "center" } },
      { value: `100%`, style: { textAlign: "center" } },
    ];
  } else if (
    selected.value.reportType === "byCategoryAndChannel" &&
    info.value?.byCategory
  ) {
    headers = [
      { title: "Categorias", style: { width: "30%" } },
      { title: "Trámites", style: {} },
      { title: "Web", style: {} },
      { title: "Aporte", style: {} },
    ];
    data = info.value.byCategory
      .map((cat) => {
        const participation =
          totalProcedures > 0
            ? (cat.totalProcedures / totalProcedures) * 100
            : 0;

        return {
          catName: {
            value: cat.categoryName,
            style: { width: "30%", textAlign: "left" },
          },
          totalProcedures: {
            value: cat.totalProcedures,
            style: { textAlign: "center" },
          },
          web: {
            value: cat.web,
            style: { textAlign: "center" },
          },
          aporte: {
            value: Math.round(participation) + "%",
            style: { textAlign: "center" },
          },
        };
      })
      .sort((a, b) => b.totalProcedures.value - a.totalProcedures.value);
    footer = [
      { value: "TOTAL", style: { width: "30%", textAlign: "center" } },
      { value: `${totalProcedures}`, style: { textAlign: "center" } },
      { value: `${datos.totalWeb}`, style: { textAlign: "center" } },
      { value: `100%`, style: { textAlign: "center" } },
    ];
  } else if (
    selected.value.reportType === "proceduresRanking" &&
    info.value?.byProcedure
  ) {
    headers = [
      { title: "Nombre", style: { width: "30%" } },
      { title: "Trámites", style: {} },
      { title: "Aporte", style: {} },
      { title: "Área", style: { width: "30%" } },
    ];
    data = info.value.byProcedure
      .map((proc) => {
        const participation =
          totalProcedures > 0
            ? (proc.totalProcedures / totalProcedures) * 100
            : 0;

        return {
          catName: {
            value: proc.procedureName,
            style: { width: "30%", textAlign: "left" },
          },
          totalProcedures: {
            value: proc.totalProcedures,
            style: { textAlign: "center" },
          },
          part: {
            value: Math.round(participation) + "%",
            style: { textAlign: "center" },
          },
          area: {
            value: proc.areaName,
            style: { width: "30%", textAlign: "left" },
          },
        };
      })
      .sort((a, b) => b.totalProcedures.value - a.totalProcedures.value);
    footer = [
      { value: "TOTAL", style: { width: "30%", textAlign: "center" } },
      { value: `${totalProcedures}`, style: { textAlign: "center" } },
      { value: `100%`, style: { textAlign: "center" } },
      { value: "", style: { width: "30%", textAlign: "center" } },
    ];
  } else {
    return { headers: [], data: [], footer: [] };
  }

  // APLICAMOS EL LÍMITE A LA TABLA TAMBIÉN (EJ: TOP 10 RANKING)
  if (limit && data.length > limit) {
    data = data.slice(0, limit);
  }

  return { headers, data, footer };
});

// const sumValues = (item) =>
//   item.values.reduce((sum, current) => sum + current, 0);

const openFirstModal = (modal, props) => {
  if (modalManagerRef.value) {
    modalManagerRef.value.closeAll();
    modalManagerRef.value.openModal(modal, props);
  }
};

// --- FUNCIÓN DE DESCARGA IMPLEMENTADA ---
const downloadReport = async () => {
  // loading.value = true;
  openFirstModal(LoadingComponent, {
    size: "auto",
    message: "Generando reporte PDF...",
    opacity: 1,
    noClose: true,
  });

  const container = document.getElementById("statistics-card");
  const controlsRow = document.getElementById("statistics-controls");
  const infoContainer = container?.querySelector(".statistics-info");
  const tableContainer = container?.querySelector(".statistics-table");
  const tableScrollBody = container?.querySelector(".table-body-scroll");
  const chartsContainer = container?.querySelector(".statistics-charts");
  const barsContainer = container?.querySelector(".stacked-bars-container");

  if (!container) {
    toast.error("No se encontró el contenido para imprimir");
    loading.value = false;
    modalManagerRef.value.closeAll();
    return;
  }

  // Guardar estilos originales
  const originalStyles = {
    container: container.style.cssText,
    controlsRow: controlsRow?.style.cssText,
    infoContainer: infoContainer?.style.cssText,
    tableContainer: tableContainer?.style.cssText,
    tableScrollBody: tableScrollBody?.style.cssText,
    chartsContainer: chartsContainer?.style.cssText,
    barsContainer: barsContainer?.style.cssText,
  };

  try {
    // --- APLICAR CAMBIOS TEMPORALES PARA PDF ---

    // Ocultar controles
    if (controlsRow) controlsRow.style.display = "none";

    // Contenedor principal
    container.style.height = "auto";
    container.style.maxHeight = "none";
    container.style.overflow = "visible";
    container.style.boxShadow = "none";

    // Layout vertical
    if (infoContainer) {
      infoContainer.style.flexDirection = "column";
      infoContainer.style.height = "auto";
      infoContainer.style.overflow = "visible";
      infoContainer.style.gap = "20px";
    }

    // Expandir tabla
    if (tableContainer) tableContainer.style.width = "100%";
    if (tableScrollBody) {
      tableScrollBody.style.maxHeight = "none";
      tableScrollBody.style.overflowY = "visible";
      tableScrollBody.style.height = "auto";
    }

    // Expandir gráficos - SOLUCIÓN CLAVE
    if (chartsContainer) {
      chartsContainer.style.width = "100%";
      chartsContainer.style.flexDirection = "column";
      chartsContainer.style.alignItems = "stretch";
    }

    if (barsContainer) {
      // Establecer ancho fijo que no exceda el PDF (aprox 800px para A4)
      barsContainer.style.width = "100%";
      barsContainer.style.maxWidth = "800px"; // Límite para no cortarse
      barsContainer.style.overflow = "visible";
      barsContainer.style.height = "auto";
      barsContainer.style.minHeight = "800px";

      // Asegurar que el contenido interno también se ajuste
      const stackedBars = barsContainer.querySelector(".stacked-bars");
      if (stackedBars) {
        stackedBars.style.width = "100%";
        stackedBars.style.minWidth = "auto";
      }
    }

    // Esperar un ciclo para que se apliquen los estilos
    await nextTick();
    await new Promise((resolve) => setTimeout(resolve, 300));

    // Formatear fecha y hora actual
    const now = new Date();
    const formattedDate = now.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    const formattedTime = now.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
    });
    const footerText = `Generado el ${formattedDate} a las ${formattedTime}`;

    // Generar PDF con header/footer
    const filename = `Reporte_${selected.value.reportType}_${Date.now()}.pdf`;
    const pdfOptions = {
      margin: [10, 0, 10, 5], // [top, left, bottom, right] - más espacio para footer
      filename: filename,
      image: { type: "jpeg", quality: 0.98 },
      html2canvas: {
        scale: 2,
        useCORS: true,
        scrollY: 0,
        logging: false,
        allowTaint: true,
        width: 800,
        windowWidth: 800,
      },
      jsPDF: {
        unit: "mm",
        format: "a4",
        orientation: "portrait",
      },
      pagebreak: { mode: ["avoid-all", "css", "legacy"] },
    };

    // Opción 1: Usar función de callback para agregar header/footer personalizado
    await html2pdf()
      .set(pdfOptions)
      .from(container)
      .toPdf()
      .get("pdf")
      .then(function (pdf) {
        const totalPages = pdf.internal.getNumberOfPages();

        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);

          // Header - Opcional
          pdf.setFontSize(10);
          pdf.setTextColor(100);
          // pdf.text("Reporte Estadístico", pdf.internal.pageSize.getWidth() / 2, 10, { align: 'center' });

          // Footer con fecha y hora
          pdf.setFontSize(8);
          pdf.setTextColor(150);
          pdf.text(
            footerText,
            pdf.internal.pageSize.getWidth() / 2,
            pdf.internal.pageSize.getHeight() - 5,
            { align: "center" },
          );

          // Número de página
          pdf.text(
            `Página ${i} de ${totalPages}`,
            pdf.internal.pageSize.getWidth() - 20,
            pdf.internal.pageSize.getHeight() - 5,
          );
        }
      })
      .save();
  } catch (error) {
    console.error("Error generando reporte:", error);
    modalManagerRef.value.closeAll();
    toast.error("Error al generar el reporte PDF");
  } finally {
    // --- RESTAURAR ESTILOS ORIGINALES ---

    // Restaurar cada elemento a su estado original
    if (controlsRow)
      controlsRow.style.cssText = originalStyles.controlsRow || "";

    container.style.cssText = originalStyles.container;

    if (infoContainer)
      infoContainer.style.cssText = originalStyles.infoContainer || "";
    if (tableContainer)
      tableContainer.style.cssText = originalStyles.tableContainer || "";
    if (tableScrollBody)
      tableScrollBody.style.cssText = originalStyles.tableScrollBody || "";
    if (chartsContainer)
      chartsContainer.style.cssText = originalStyles.chartsContainer || "";
    if (barsContainer)
      barsContainer.style.cssText = originalStyles.barsContainer || "";

    // Forzar redibujado de gráficos
    stackedBarKey.value++;

    // Pequeña pausa para asegurar restauración
    await new Promise((resolve) => setTimeout(resolve, 100));

    loading.value = false;
    modalManagerRef.value.closeAll();
  }
};
</script>

<style scoped>
@import "@/components/Utils/css/table-styles.css";

.content-container {
  overflow: visible !important;
}

.column-content-container {
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: flex-start;
  gap: 1.56rem;
  width: 98%;
}

.options-row {
  display: flex;
  flex-direction: row;
  gap: 0.62rem;
  margin-top: 1.5rem;
}

.options-row button {
  width: auto;
  padding: 0.62rem 1.25rem;
  background-color: #cdcdcd;
  border: none;
  border-radius: 1.25rem;
}

.options-row button.activo {
  background: var(--Verde-claro, rgba(0, 195, 168, 0.2));
}

/*.simplified-muni-table {
  table-layout: unset;
}*/

.simplified-muni-table tbody tr td:first-child {
  padding-left: 0.6rem;
}

.simplified-muni-table tbody tr td:last-child {
  padding-right: 0.6rem;
}

.statistics-card {
  display: flex;
  flex-direction: column;
  height: 68vh;
  max-height: 68vh;

  border-radius: 0.625rem;
  background: rgba(255, 255, 255, 0.7);
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.25);
  padding: 0.625rem 0.625rem 0.625rem 2.1875rem;
  overflow: hidden;
}

.statistics-btns-row {
  display: flex;
  flex-direction: row;
  padding: 0.8rem 0rem;
  align-items: center;
  justify-content: space-between;
}

.statistics-btns-row .options {
  display: flex;
  gap: 0.87rem;
}

.statistics-info {
  display: flex;
  flex-direction: row;
  flex: 1; /* Esto está bien, permite que el contenedor crezca */
  width: 100%;
  height: 100%;
  gap: 1rem;
  overflow: hidden;
}

.no-data {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
}

.statistics-table {
  /* No se cambia */
  margin-top: 0.62rem;
  width: 30rem;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  direction: rtl;
}
.content-wrapper {
  direction: ltr;
}
.content-wrapper p {
  padding: 0rem 0.44rem;
}
.table-body-scroll {
  max-height: 35.2vh;
  overflow-y: auto;
  scrollbar-width: none;
}

/* Container for the charts */
.statistics-charts {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  /* CAMBIO CLAVE: Usa un ancho fijo para que no se expanda ilimitadamente */
  width: calc(
    100% - 25rem - 1rem
  ); /* 100% del padre, menos el ancho de la tabla y el gap */
  /*gap: 1rem;*/
}

.pie {
  width: 100%;
  flex-basis: 40%;
  min-height: 100px;
}

.stacked-bars-container {
  /* CAMBIO CLAVE: Quita el width: 100% si el chart ya lo tiene */
  overflow-x: auto;
  overflow-y: hidden;
  flex-basis: 60%;
  min-height: 150px;
  /* Mantenemos esta propiedad, es muy importante para el scroll */
  min-width: 0;
}

.full-height-chart {
  flex-basis: 100%;
}

.stacked-bars {
  height: 100%;
}
.truncated-text {
  /* Permite que el texto salte de línea */
  white-space: normal;
  /* Configuración de WebKit para limitar las líneas */
  display: -webkit-box;
  -webkit-line-clamp: 2; /* Limita a 2 líneas */
  -webkit-box-orient: vertical;
  /* Oculta el texto que se desborde */
  overflow: hidden;
  /* Muestra los puntos suspensivos al final */
  text-overflow: ellipsis;
}

#table-footer {
  margin-top: 0;
  margin-bottom: -0.62rem;
}
#table-footer th::after {
  bottom: auto;
  top: 0;
}
.statistics-btns-row button {
  width: auto;
  border-radius: 1.25rem;
  color: var(--text-primary);
  font-weight: bold;

  padding: 0.3rem 1.25rem;

  display: flex;
  justify-content: center;
  align-items: center;
}

.period-btn-container {
  margin-right: 7rem;
}

button.period-btn {
  padding: 0.3rem 0.3rem;
  border: none;
  background: var(--Gris-1, #dedede);
  gap: 0.3rem;
}

.muni-section-btn {
  border: 0.5px solid var(--text-primary);
  background: var(--bg-popup);
}

.ia-btn {
  border: none;
  background: var(--bg-popup);
}

.ia-btn.rose {
  box-shadow: 0 0 6.6px 0 #e15bf5;
}

.ia-btn.green {
  box-shadow: 0 0 6.6px 0 var(--color-success);
}
</style>
